
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import EditableItem from '~/components/RemoteFeatureFlags/Groups/EditableItem.vue';
import FlagsSelector from '~/components/RemoteFeatureFlags/Groups/FlagsSelector.vue';
import UsersSelector from '~/components/RemoteFeatureFlags/Groups/UsersSelector.vue';
import { FlagGroup, LocalFlagGroup } from '~/interfaces/user.interface';
import UserRightsMixin from '~/mixins/UserRightsMixin';
import { RemoteFeatureFlagsModule } from '~/store/modules/remoteFeatureFlags';
import DarkThemeInfoMixin from '~common-mixins/DarkThemeInfoMixin';

@Component({
    components: {
        EditableItem,
        FlagsSelector,
        UsersSelector
    }
})
export default class GroupEditor extends Mixins(DarkThemeInfoMixin, UserRightsMixin) {
    @Prop({ required: true }) group!: FlagGroup;
    @Prop({ default: false }) value!: boolean;

    localGroup: LocalFlagGroup = this.createLocalGroup();
    savedGroupName = this.group?.name || '';
    isUpdatingGroupName = false;

    get dialog() {
        return this.value;
    }

    set dialog(value: boolean) {
        this.$emit('input', value);
    }

    @Watch('group', { deep: true, immediate: true })
    onGroupChange() {
        this.localGroup = this.createLocalGroup();
        this.savedGroupName = this.group.name;
    }

    createLocalGroup(): LocalFlagGroup {
        return {
            ...this.group,
            isValid: true,
            isEditable: true
        };
    }

    closeDialog() {
        this.dialog = false;
        this.localGroup = this.createLocalGroup();
        this.savedGroupName = this.group.name;
    }

    async patchGroupName(): Promise<void> {
        if (this.localGroup.name === this.savedGroupName) {
            return;
        }

        this.isUpdatingGroupName = true;
        try {
            await RemoteFeatureFlagsModule.updateGroup({
                group_id: this.localGroup.id,
                name: this.localGroup.name,
            });
            this.savedGroupName = this.localGroup.name;
        } catch (error) {
            console.error('Error updating group name', error);
        } finally {
            this.isUpdatingGroupName = false;
        }
    }

    get flagIds() {
        return this.localGroup.flags.map(flag => flag.id);
    }

    async addUser(email: string) {
        try {
            await RemoteFeatureFlagsModule.addUserToGroup({
                group_id: this.group.id,
                user_email: email
            });
            // The store will update with the new group data from API
        } catch (error) {
            console.error('Error adding user to group', error);
        }
    }

    async removeUser(user_id: string) {
        try {
            await RemoteFeatureFlagsModule.removeUserFromGroup({
                group_id: this.group.id,
                user_id
            });
            // The store will update with the new group data from API
        } catch (error) {
            console.error('Error removing user from group', error);
        }
    }

    async addFlag(flagId: string) {
        try {
            await RemoteFeatureFlagsModule.addFlagToGroup({
                group_id: this.group.id,
                flag_id: flagId
            });
            // The store will update with the new group data from API
        } catch (error) {
            console.error('Error adding flag to group', error);
        }
    }

    async removeFlag(flagId: string) {
        try {
            await RemoteFeatureFlagsModule.removeFlagFromGroup({
                group_id: this.group.id,
                flag_id: flagId
            });
            // The store will update with the new group data from API
        } catch (error) {
            console.error('Error removing flag from group', error);
        }
    }

    async saveChanges() {
        try {
            await RemoteFeatureFlagsModule.updateGroup({
                group_id: this.localGroup.id,
                name: this.localGroup.name,
            });
            this.closeDialog();
        } catch (error) {
            console.error('Error saving group changes', error);
        }
    }

    async deleteGroup() {
        if (confirm('Are you sure you want to delete this group? This action cannot be undone.')) {
            try {
                await RemoteFeatureFlagsModule.deleteGroup(this.group.id);
                this.closeDialog();
            } catch (error) {
                console.error('Error deleting group', error);
            }
        }
    }
}
